"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ArrowLeft, Boxes, ChevronRight, Cpu, Database, Gamepad2, Globe, LogOut, Menu, Server, ShieldAlert, ShieldCheck, Users, X } from "lucide-react";
import { useSession } from "@/components/session-provider";
import { ErrorNotice, IconButton } from "@/components/ui";
import { errorMessage } from "@/lib/api";

export function AdminShell({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const desktop = window.matchMedia("(min-width: 801px)");
    const resize = () => { if (desktop.matches) setOpen(false); };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        document.getElementById("navigation-toggle")?.focus();
      }
    };
    desktop.addEventListener("change", resize);
    window.addEventListener("keydown", escape);
    resize();
    return () => {
      desktop.removeEventListener("change", resize);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);

  if (!session.user) return null;
  const links = [
    { href: "/admin/servers", label: "Servers", icon: Gamepad2, permission: "server.read.all" },
    { href: "/admin/nodes", label: "Nodes", icon: Server, permission: "node.read" },
    { href: "/admin/locations", label: "Locations", icon: Globe, permission: "node.read" },
    { href: "/admin/databases", label: "Databases", icon: Database, permission: "node.read" },
    { href: "/admin/templates", label: "Templates", icon: Boxes, permission: "egg.read" },
    { href: "/admin/users", label: "Users", icon: Users, permission: "user.read" },
  ].filter((link) => session.tenant && session.can(link.permission));

  if (!links.length) return <main className="boot-screen"><ShieldAlert size={28} /><span>Workspace administrator access required</span><Link href="/" className="button"><ArrowLeft size={16} />Back to workspace</Link></main>;

  const section = links.find((link) => pathname.startsWith(link.href))?.label || "Administration";
  return <div className="panel-shell admin-area">
    {open && <button className="nav-backdrop" aria-label="Close navigation" onClick={() => setOpen(false)} />}
    <aside id="panel-navigation" className={`sidebar ${open ? "sidebar-open" : ""}`}>
      <Link href={links[0].href} className="brand" onClick={() => setOpen(false)}><ShieldCheck size={24} /><span>Velnqo<span className="brand-suffix">Workspace Admin</span></span></Link>
      <IconButton className="mobile-nav-close" label="Close navigation" onClick={() => setOpen(false)}><X size={18} /></IconButton>
      <nav aria-label="Workspace administration navigation">
        <Link href="/" onClick={() => setOpen(false)} className="nav-item nav-switch"><ArrowLeft size={18} />Back to workspace</Link>
        <div className="nav-label nav-label-admin">Workspace administration</div>
        {links.map(({ href, label, icon: Icon }) => <Link key={href} href={href} onClick={() => setOpen(false)} aria-current={pathname.startsWith(href) ? "page" : undefined} className={`nav-item ${pathname.startsWith(href) ? "nav-active" : ""}`}><Icon size={18} />{label}</Link>)}
      </nav>
      <div className="sidebar-bottom"><div className="account">
        <Link href="/settings/account" className="account-link"><span className="avatar">{(session.user.name || session.user.username).slice(0, 2).toUpperCase()}</span><div><strong>{session.user.name || session.user.username}</strong><small>Workspace administrator</small></div></Link>
        <IconButton label="Sign out" onClick={async () => { try { await session.logout(); } catch (error) { setError(errorMessage(error)); } }}><LogOut size={16} /></IconButton>
      </div></div>
    </aside>
    <div className="panel-main" inert={open}>
      <header className="topbar"><IconButton className="mobile-nav-toggle" id="navigation-toggle" aria-expanded={open} aria-controls="panel-navigation" label="Open navigation" onClick={() => setOpen(true)}><Menu size={20} /></IconButton><div className="breadcrumbs"><Cpu size={16} /><span>Workspace admin</span><ChevronRight size={14} /><strong>{section}</strong></div><span className="workspace-label"><span className="connection-dot" />{session.tenant?.name || "Workspace"}</span></header>
      <main className="page-content"><ErrorNotice message={error} />{children}</main>
    </div>
  </div>;
}
